import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { motion } from 'framer-motion';
import { Copy, Check, Sparkles, Loader2, BookOpen, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import FollowUpTimeline from './FollowUpTimeline';
import ExportShareMenu from './ExportShareMenu';
import { getCategoryBadgeStyle, getCategoryHex } from '../utils/categoryColors';

const AnalysisModal = ({ analysis, onClose, downloadText, download, onFollowUpRequested }) => {
  const [copiedPrayer, setCopiedPrayer] = useState(false);
  const [deeperAnalysis, setDeeperAnalysis] = useState(null);
  const [isLoadingDeeper, setIsLoadingDeeper] = useState(false);

  if (!analysis) return null;

  const handleCopyPrayer = () => {
    const prayerText = `${analysis.prayer.paragraph1}\n\n${analysis.prayer.paragraph2}`;
    navigator.clipboard.writeText(prayerText);
    setCopiedPrayer(true);
    toast.success('Prayer copied to clipboard');
    setTimeout(() => setCopiedPrayer(false), 2000);
  };

  const buildFallbackText = () => {
    if (!analysis) return '';
    const parts = [];
    if (analysis.category) parts.push(`Category: ${analysis.category}`);
    if (analysis.themes && analysis.themes.length) {
      parts.push(`Themes: ${analysis.themes.join(', ')}`);
    }
    if (analysis.insights && analysis.insights.length) {
      parts.push(`Previous insights:\n- ${analysis.insights.join('\n- ')}`);
    }
    if (analysis.verse && analysis.verse.reference) {
      parts.push(`Previous scripture: ${analysis.verse.reference}`);
    }
    return parts.join('\n\n');
  };

  const hasPlainText =
    typeof downloadText === 'string' && downloadText.trim().length > 0;

  const deeperText = hasPlainText ? downloadText.trim() : buildFallbackText();
  const deeperAvailable = deeperText.trim().length >= 10;

  const handleDeeperAnalysis = async () => {
    if (!deeperAvailable) {
      toast.error('Deeper analysis is not available for this entry.', {
        description:
          "We couldn't read the original text. Try a newer entry or add a follow-up reflection first.",
      });
      return;
    }

    setIsLoadingDeeper(true);
    try {
      const result = await api.analyzeText(deeperText, true, download?.id);
      setDeeperAnalysis(result);
      toast.success('Deeper analysis complete!', {
        description:
          download?.follow_ups?.length
            ? 'Includes your follow-up reflections and patterns from your journey.'
            : 'Patterns from your journey are included when available.',
      });
    } catch (error) {
      console.error('Deeper analysis error:', error);
      toast.error('Failed to generate deeper analysis', {
        description: error?.message || 'Please try again in a moment.',
      });
    } finally {
      setIsLoadingDeeper(false);
    }
  };

  const currentAnalysis = deeperAnalysis || analysis;
  const categoryHex = getCategoryHex(currentAnalysis.category);

  // Compute a subtle darker + lighter shade for hover/border variants.
  // We keep this cheap \u2014 stack the category hex as an rgba tint at a couple
  // of alpha steps. Modal root sets --sd-primary so the entire dialog blooms
  // into the category color (Done button, scripture border, sparkle, etc.).
  const localAccentStyle = {
    '--sd-primary': categoryHex,
    '--sd-primary-fg': '#1C1E21',
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl h-[90vh] p-0 glass-card"
        data-testid="analysis-modal"
        style={localAccentStyle}
      >
        <div className="flex flex-col h-full max-h-full overflow-hidden">
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[var(--sd-border)]">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle className="text-2xl font-semibold mb-2 flex items-center gap-2 text-[var(--sd-fg)]">
                  <Sparkles size={24} className="text-[var(--sd-primary)]" />
                  Your Spiritual Insights
                </DialogTitle>
                {/* AI transparency caption — Apple 5.5 / Google Play AI-generated content disclosure */}
                <p
                  className="text-[11px] uppercase tracking-[0.18em] text-[var(--sd-muted-fg)] mb-3"
                  data-testid="analysis-modal-ai-disclosure"
                >
                  Analyzed with AI &middot; Human-reviewed Scripture matching
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge
                    style={getCategoryBadgeStyle(currentAnalysis.category)}
                    className="border font-semibold"
                  >
                    {currentAnalysis.category}
                  </Badge>
                  <span className="text-xs text-[var(--sd-muted-fg)]">
                    {new Date().toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                  {deeperAnalysis && (
                    <Badge className="bg-[var(--sd-primary)]/20 text-[var(--sd-primary)] border-[var(--sd-primary)]/40">
                      Deeper Analysis
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Content */}
          <ScrollArea className="flex-1 overflow-y-auto insights-scrollarea">
            <div className="px-6 py-6 space-y-8">
              {/* Original Download Text */}
              {downloadText && (
                <motion.section
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                  className="p-5 bg-[var(--sd-surface)] rounded-xl border border-[var(--sd-border)]"
                >
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--sd-fg)] mb-3 flex items-center gap-2">
                    <MessageSquarePlus size={16} />
                    Your Download
                  </h3>
                  <p className="text-base leading-relaxed text-[var(--sd-fg)]" data-testid="analysis-modal-original-text">
                    {downloadText}
                  </p>
                </motion.section>
              )}

              {/* Themes */}
              <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--sd-fg)] mb-3">
                  Themes
                </h3>
                <div className="flex flex-wrap gap-2">
                  {currentAnalysis.themes.map((theme, idx) => (
                    <Badge
                      key={idx}
                      style={{
                        backgroundColor: `${categoryHex}20`,
                        borderColor: `${categoryHex}66`,
                        color: 'var(--sd-fg)',
                      }}
                      className="border font-medium"
                    >
                      {theme}
                    </Badge>
                  ))}
                </div>
              </motion.section>

              <Separator className="bg-[var(--sd-border)]" />

              {/* Insights */}
              <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--sd-fg)] mb-4">
                  3 Insights for You
                </h3>
                <div className="space-y-4">
                  {currentAnalysis.insights.map((insight, idx) => (
                    <div
                      key={idx}
                      className="p-4 bg-[var(--sd-surface)] rounded-xl border border-[var(--sd-border)]"
                    >
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--sd-primary)] text-[var(--sd-primary-fg)] flex items-center justify-center text-sm font-semibold">
                          {idx + 1}
                        </span>
                        <p className="text-base leading-relaxed text-[var(--sd-fg)] font-medium">{insight}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Applicable Bible Verse — Under Insights */}
                <div
                  className="mt-6 p-5 rounded-xl border-2"
                  style={{
                    backgroundColor: 'var(--sd-surface)',
                    borderColor: 'var(--sd-primary)',
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen size={18} className="text-[var(--sd-primary)]" />
                    <h4 className="text-sm font-semibold text-[var(--sd-primary)] uppercase tracking-wide">Applicable Scripture</h4>
                  </div>
                  <p
                    className="font-scripture text-lg leading-relaxed mb-3 text-[var(--sd-fg)]"
                    data-testid="analysis-modal-scripture-text"
                  >
                    "{currentAnalysis.verse.text}"
                  </p>
                  <p
                    className="text-sm text-[var(--sd-fg)] font-semibold font-scripture"
                    data-testid="analysis-modal-scripture-reference"
                  >
                    — {currentAnalysis.verse.reference} ({currentAnalysis.verse.translation})
                  </p>
                </div>

                {/* Deeper Analysis Button */}
                {!deeperAnalysis && (
                  <div className="mt-4 text-center">
                    <Button
                      onClick={handleDeeperAnalysis}
                      disabled={isLoadingDeeper || !deeperAvailable}
                      variant="outline"
                      className="border-[var(--sd-primary)] text-[var(--sd-primary)] hover:bg-[var(--sd-primary)]/10 disabled:opacity-50"
                      data-testid="deeper-analysis-button"
                      title={
                        !deeperAvailable
                          ? "We couldn't read the original text for this entry."
                          : 'Regenerate with richer, more detailed insights'
                      }
                    >
                      {isLoadingDeeper ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Analyzing deeper...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} className="mr-2" />
                          Request Deeper AI Analysis
                        </>
                      )}
                    </Button>
                    {!deeperAvailable && (
                      <p className="text-xs text-[var(--sd-muted-fg)] mt-2">
                        Original text unavailable for this entry.
                      </p>
                    )}
                  </div>
                )}
              </motion.section>

              <Separator className="bg-[var(--sd-border)]" />

              {/* Prayer */}
              <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--sd-fg)]">
                    Your Activation Prayer
                  </h3>
                  <Button
                    onClick={handleCopyPrayer}
                    variant="ghost"
                    size="sm"
                    className="text-xs text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)]"
                    data-testid="copy-prayer-button"
                  >
                    {copiedPrayer ? (
                      <>
                        <Check size={14} className="mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={14} className="mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <div className="p-6 bg-[var(--sd-surface)] rounded-xl border border-[var(--sd-border)] space-y-4">
                  <p className="text-base leading-relaxed text-[var(--sd-fg)] font-medium">{currentAnalysis.prayer.paragraph1}</p>
                  <p className="text-base leading-relaxed text-[var(--sd-fg)] font-medium">{currentAnalysis.prayer.paragraph2}</p>
                  <p className="text-sm text-[var(--sd-muted-fg)] italic text-right">
                    — Amen
                  </p>
                </div>
              </motion.section>
            </div>

            {/* Follow-Up Timeline */}
            {download && download.follow_ups && download.follow_ups.length > 0 && (
              <>
                <Separator className="bg-[var(--sd-border)]" />
                <div className="py-6">
                  <FollowUpTimeline
                    followUps={download.follow_ups}
                    originalDate={download.created_at}
                  />
                </div>
              </>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[var(--sd-border)] flex flex-wrap justify-between items-center gap-3">
            {onFollowUpRequested && (
              <Button
                onClick={() => {
                  onClose();
                  onFollowUpRequested();
                }}
                variant="outline"
                className="border-[var(--sd-primary)] text-[var(--sd-primary)] hover:bg-[var(--sd-primary)]/10"
              >
                <MessageSquarePlus size={16} className="mr-2" />
                Add Follow-Up
              </Button>
            )}
            <div className="flex-1" />
            <ExportShareMenu
              entry={{
                downloadText,
                analysis: currentAnalysis,
                createdAt: download?.created_at,
              }}
            />
            <Button
              onClick={onClose}
              className="bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)]"
              data-testid="analysis-modal-done-button"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnalysisModal;
